import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Clock, Send, ShieldAlert, Trash2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useSOS } from "../../context/SOSContext";
import { clearQueue, enqueueStatus, readQueue, syncEmergencyQueue } from "../../lib/sosQueue";

const QUICK_CHIPS = [
  { id: "safe", label: "Estoy a salvo", text: "Estoy a salvo.", origin: "safe" },
  { id: "help", label: "Auxilio en...", text: "Auxilio en ", origin: "help" },
];

function _fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
  } catch (_e) {
    return "";
  }
}

export function SOSStatusQueue() {
  const { user } = useAuth();
  const { isOnline, queueSize } = useSOS();
  const [draft, setDraft] = useState("");
  const [items, setItems] = useState(() => readQueue());

  // Keep local list in sync with the storage queue (changes from other
  // components / tabs are propagated via `sos.queue.change`).
  useEffect(() => {
    const refresh = () => setItems(readQueue());
    window.addEventListener("sos.queue.change", refresh);
    const onSynced = (e) => {
      const count = e?.detail?.count || 0;
      if (count > 0) {
        toast.success(`${count} mensaje${count === 1 ? "" : "s"} de estado sincronizado${count === 1 ? "" : "s"}.`);
      }
      refresh();
    };
    window.addEventListener("sos.synced", onSynced);
    return () => {
      window.removeEventListener("sos.queue.change", refresh);
      window.removeEventListener("sos.synced", onSynced);
    };
  }, []);

  const addEntry = useCallback(
    (text, origin = "custom") => {
      const trimmed = (text || "").trim();
      if (!trimmed) return;
      enqueueStatus({ text: trimmed, user_id: user?.id || null, origin });
      setItems(readQueue());
      setDraft("");
      if (isOnline) {
        // Opportunistic sync — fire-and-forget.
        syncEmergencyQueue().catch(() => {});
      }
    },
    [isOnline, user]
  );

  const onChip = (chip) => {
    if (chip.origin === "help") {
      // Pre-fill — user types the location (TEXT only, never GPS)
      setDraft(chip.text);
    } else {
      addEntry(chip.text, chip.origin);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    addEntry(draft, "custom");
  };

  const clearAll = () => {
    clearQueue();
    setItems([]);
  };

  const trySyncNow = async () => {
    const res = await syncEmergencyQueue();
    if (res.offline) {
      toast.info("Sin conexión — los mensajes se enviarán cuando regrese el internet.");
    } else if (res.confirmed > 0) {
      // toast appears via `sos.synced` event listener above
    } else if (res.attempted === 0) {
      toast.info("No hay mensajes en cola.");
    } else {
      toast.error("No se pudo sincronizar. Reintentando en segundo plano.");
    }
    setItems(readQueue());
  };

  return (
    <div
      className="q-glass border border-white/10 rounded-2xl p-4 sm:p-5"
      data-testid="sos-status-queue"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <h3 className="font-display text-base font-bold tracking-tight text-slate-100 flex items-center gap-2">
          <ShieldAlert size={16} className="text-red-400" /> Mensajes de estado
        </h3>
        <span
          className={
            "text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full " +
            (isOnline
              ? "text-emerald-300 bg-emerald-500/10 border border-emerald-400/30"
              : "text-amber-300 bg-amber-500/10 border border-amber-400/30")
          }
          data-testid="sos-connection-status"
        >
          {isOnline ? "ONLINE" : "OFFLINE"}
        </span>
      </div>
      <p className="text-[11px] text-slate-400 mb-3">
        Los mensajes se guardan en tu teléfono y se envían automáticamente cuando regrese el internet. Sin GPS, sin localización — tú escribes el texto.
      </p>

      <div className="flex flex-wrap gap-2 mb-3">
        {QUICK_CHIPS.map((chip) => (
          <button
            key={chip.id}
            type="button"
            onClick={() => onChip(chip)}
            data-testid={`sos-chip-${chip.id}`}
            className="text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-full border border-red-400/30 text-red-200 bg-red-500/10 hover:bg-red-500/20"
          >
            {chip.label}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="flex gap-2 mb-3">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Escribe tu estado…"
          maxLength={500}
          className="q-input flex-1 text-sm"
          data-testid="sos-status-input"
        />
        <button
          type="submit"
          data-testid="sos-status-submit"
          className="q-btn-primary inline-flex items-center gap-2 !py-2 !px-3"
          aria-label="Agregar a la cola"
        >
          <Send size={14} />
        </button>
      </form>

      {items.length > 0 ? (
        <>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
              En cola: {queueSize || items.length}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={trySyncNow}
                data-testid="sos-sync-now"
                className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 hover:text-cyan-200"
              >
                Sincronizar ahora
              </button>
              <button
                type="button"
                onClick={clearAll}
                data-testid="sos-clear-queue"
                className="text-[10px] font-mono uppercase tracking-widest text-red-300 hover:text-red-200 inline-flex items-center gap-1"
              >
                <Trash2 size={10} /> Limpiar
              </button>
            </div>
          </div>
          <ul className="space-y-1.5 max-h-48 overflow-y-auto">
            {items.slice().reverse().map((it) => (
              <li
                key={it.id}
                className="flex items-start gap-2 text-sm text-slate-200 rounded-lg bg-black/20 border border-white/5 px-2.5 py-1.5"
                data-testid={`sos-queue-item-${it.origin}`}
              >
                <Clock size={11} className="shrink-0 text-slate-500 mt-1" />
                <span className="flex-1 min-w-0 break-words">{it.text}</span>
                <span className="text-[10px] font-mono text-slate-500 shrink-0">
                  {_fmtTime(it.timestamp)}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div
          className="rounded-lg border border-white/5 bg-black/15 px-3 py-4 text-center text-xs text-slate-500 inline-flex items-center justify-center gap-2 w-full"
          data-testid="sos-queue-empty"
        >
          <CheckCircle2 size={14} className="text-emerald-400" />
          No hay mensajes en cola.
        </div>
      )}
    </div>
  );
}
